import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { verifyQr } from '../api/odoo';
import { safeErrorMessage } from '../utils/errorMessage';
import './ScanPage.css';

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

export default function ScanPage({ onPartnerLoaded = undefined }) {
  const [status, setStatus] = useState('captcha');
  const [captchaReady, setCaptchaReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [partner, setPartner] = useState(null);
  const widgetRef = useRef(null);
  const widgetId = useRef(null);

  function doVerify(token) {
    if (!token) return;
    setStatus('verifying');

    const params = new URLSearchParams(window.location.search);
    const partnerId = params.get('partner_id');
    const qrToken = params.get('token');

    if (!partnerId || !qrToken || !/^\d+$/.test(partnerId)) {
      setStatus('error');
      setErrorMsg('Invalid QR code.');
      return;
    }

    verifyQr(partnerId, qrToken)
      .then((p) => {
        sessionStorage.setItem('scanned_partner', JSON.stringify(p));
        setPartner(p);
        setStatus('success');
        if (onPartnerLoaded) onPartnerLoaded(p);
      })
      .catch((e) => {
        setStatus('error');
        setErrorMsg(safeErrorMessage(e, 'QR verification failed.'));
        if (window.grecaptcha && widgetId.current !== null) {
          window.grecaptcha.reset(widgetId.current);
        }
      });
  }

  useEffect(() => {
    if (!SITE_KEY) return;

    // Stable global name — reCAPTCHA stores it by string, so it must not change
    window.recaptchaOnLoad = () => {
      if (widgetRef.current && widgetId.current === null) {
        widgetId.current = window.grecaptcha.render(widgetRef.current, {
          sitekey: SITE_KEY,
          callback: doVerify,
          'expired-callback': () => {
            if (widgetId.current !== null) window.grecaptcha.reset(widgetId.current);
          },
        });
        setCaptchaReady(true);
      }
    };

    const existing = document.getElementById('recaptcha-script');
    if (!existing) {
      const script = document.createElement('script');
      script.id = 'recaptcha-script';
      script.src = 'https://www.google.com/recaptcha/api.js?onload=recaptchaOnLoad&render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    } else if (window.grecaptcha && window.grecaptcha.render && widgetId.current === null) {
      window.recaptchaOnLoad();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="scp-root">
      <div className="scp-card">
        {status === 'captcha' && (
          <>
            <div className="scp-icon scp-icon--shield">🛡️</div>
            <h2 className="scp-title">Human Verification</h2>
            <p className="scp-sub">Please complete the check below to continue</p>

            {SITE_KEY ? (
              <div className="scp-captcha-wrap">
                {!captchaReady && <p className="scp-captcha-loading">Loading verification…</p>}
                <div ref={widgetRef} />
              </div>
            ) : (
              <div className="scp-captcha-missing">
                <p><code>REACT_APP_RECAPTCHA_SITE_KEY</code> is not set in <code>.env</code></p>
                <button type="button" className="scp-bypass-btn" onClick={() => doVerify('dev-bypass')}>
                  Continue (dev mode — no key)
                </button>
              </div>
            )}
          </>
        )}

        {status === 'verifying' && (
          <>
            <div className="scp-spin" />
            <h2 className="scp-title">Verifying QR Code…</h2>
            <p className="scp-sub">Please wait</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="scp-icon scp-icon--ok">✓</div>
            <h2 className="scp-title scp-title--ok">Verified!</h2>
            <p className="scp-sub">Welcome, {partner?.name}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="scp-icon scp-icon--err">✕</div>
            <h2 className="scp-title scp-title--err">Scan Failed</h2>
            <p className="scp-sub">{errorMsg}</p>
          </>
        )}
      </div>
    </div>
  );
}

ScanPage.propTypes = {
  onPartnerLoaded: PropTypes.func,
};
